import { useEffect, useRef, useState } from "react";
import { ROLES, SUMMARIZE_STATES } from "../constants";
import { fetchScheduledAgents, enableExtension } from "../utils/agenticExtensionUtils";
import LogManager from "../utils/logManager";

/**
 * Auto-activates the agents an organizer selected at schedule time (on the meetings
 * website) once the AiMS session is active.
 *
 * This hook never starts RTMS. It only reacts to a session that is already active (summarizeState === SUMMARIZING), 
 * however it started — auto-start-summary or a manual click. If no session starts, nothing activates
 * (the agents would have no transcript/events to consume anyway).
 *
 * Two guarded phases, ordered independently so whichever finishes last drives activation:
 *   Phase 1 (fetch): organizer-only, as soon as meetingId is known (host/coHost get it at
 *     init). Runs in PARALLEL with the other in-meeting init checks so the list is ready
 *     before the session goes active — no added latency on the critical path.
 *   Phase 2 (activate): once the session is active AND aimsUuid (rtmsStreamId) exists,
 *     activate each scheduled agent. Best-effort per agent — partial failures are
 *     surfaced via onError, and successes are reported via onAgentsActivated.
 *
 * The ScheduledAgents record is keyed by (meetingId, organizerAlias); the router injects
 * the caller's alias server-side, so only the actual organizer's lookup matches — an
 * attendee (or a co-host who did not schedule) gets an empty list.
 *
 * Known Phase-1 limitation (see design): attendees already in the meeting do not get a
 * live push when these activate — they see the agents on popover-open / reload.
 *
 * @param {object}   params
 * @param {boolean}  params.isAssistantEnabled  Assistant on (off for country-blocked users).
 * @param {string}   params.role               AiMS meeting role (ROLES.*).
 * @param {string}   params.meetingId          Zoom numeric meeting ID (host/coHost only).
 * @param {string}   params.meetingUuid        Zoom per-instance meeting UUID.
 * @param {string}   params.aimsUuid           RTMS stream ID; present once session active.
 * @param {string}   params.summarizeState     SUMMARIZE_STATES.*.
 * @param {function} params.onAgentsActivated  (activatedIds: string[]) => void, called after
 *                                              the activation loop with the ids that succeeded.
 * @param {function} params.onError            (info) => void, called when auto-activation could
 *   not fully complete. `info` is one of:
 *     { scope: "all" } — the scheduled list failed to load OR every scheduled agent failed to activate.
 *     { scope: "partial", failedIds: string[] } — some agents activated, some failed.
 *   The parent maps this to a customer-facing toast (resolving failedIds → display names).
 */
const useAutoActivateScheduledAgents = ({
  isAssistantEnabled,
  role,
  meetingId,
  meetingUuid,
  aimsUuid,
  summarizeState,
  onAgentsActivated,
  onError,
}) => {
  // null = not yet resolved; [] = none scheduled; [...] = selection.
  const [scheduledIds, setScheduledIds] = useState(null);
  // true once a fetch attempt has definitively failed (distinct from "still pending"). Drives
  // the Case-1 "all inactive" toast, surfaced from Phase 2 so it only shows once in-session.
  const [fetchFailed, setFetchFailed] = useState(false);
  const fetchGuardRef = useRef(false);
  const activateGuardRef = useRef(false);
  const onAgentsActivatedRef = useRef(onAgentsActivated);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onAgentsActivatedRef.current = onAgentsActivated;
  }, [onAgentsActivated]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Auto-activation fires ONCE per page mount. `activateGuardRef` latches on the first
  // activation and is never reset, so ending the session and starting another one within the
  // same mount does NOT re-activate the scheduled agents (they stay off unless toggled on
  // manually).

  // Phase 1 — fetch the schedule-time selection early, in parallel with other init checks.
  useEffect(() => {
    if (!isAssistantEnabled) return;
    if (role !== ROLES.ORGANIZER) {
      LogManager.info("Auto-activate scheduled agents: skipped fetch, role =", role);
      return;
    }
    if (!meetingId) {
      // Numeric meetingId is host/coHost-only; without it we cannot query ScheduledAgents.
      LogManager.info("Auto-activate scheduled agents: skipped fetch, meetingId unavailable");
      return;
    }
    if (fetchGuardRef.current) return;
    fetchGuardRef.current = true;

    LogManager.info("Auto-activate scheduled agents: fetching selection for meetingId:", meetingId);
    fetchScheduledAgents(meetingId).then((extensionIds) => {
      // null => fetch failed; leave scheduledIds null so Phase 2 does not activate a guess.
      if (Array.isArray(extensionIds)) {
        LogManager.info(`Auto-activate scheduled agents: ${extensionIds.length} scheduled`);
        LogManager.metric("ScheduledAgents_Fetched", extensionIds.length);
        setScheduledIds(extensionIds);
      } else {
        LogManager.error("Auto-activate scheduled agents: fetch failed, will not activate");
        LogManager.metric("ScheduledAgents_FetchFailed", 1);
        // Case 1 (terminal): the list never loaded. Flag it so Phase 2 surfaces the
        // "all inactive" toast once the session is active (that toast points to Settings,
        // which only exists in-session). fetchGuardRef stays latched — we fetch exactly once.
        setFetchFailed(true);
      }
    });
  }, [isAssistantEnabled, role, meetingId]);

  // Phase 2 — activate once the session is active and the fetch has settled (resolved list OR
  // a known failure). Re-runs when the session turns active, the fetch completes, or the fetch
  // fails, whichever is last. Emits one of three onError shapes (see the onError param doc).
  useEffect(() => {
    if (!isAssistantEnabled) return;
    if (role !== ROLES.ORGANIZER) return;
    if (summarizeState !== SUMMARIZE_STATES.SUMMARIZING) return;
    if (!meetingUuid || !meetingId || !aimsUuid) return;
    if (!fetchFailed && scheduledIds === null) return; // fetch still pending — wait
    if (activateGuardRef.current) return;
    activateGuardRef.current = true;

    // Case 1 — the scheduled list never loaded. Nothing was activated.
    if (fetchFailed) {
      LogManager.error("Auto-activate scheduled agents: list fetch failed, nothing activated");
      onErrorRef.current?.({ scope: "all" });
      return;
    }

    if (scheduledIds.length === 0) {
      LogManager.info("Auto-activate scheduled agents: none scheduled, nothing to activate");
      return;
    }

    const activate = async () => {
      LogManager.info(`Auto-activate scheduled agents: activating ${scheduledIds.length} agents`);
      LogManager.metric("ScheduledAgents_ActivateTriggered", 1);
      // Best-effort per agent — one failure must not block the rest.
      const results = await Promise.all(
        scheduledIds.map((id) => enableExtension(meetingUuid, meetingId, id, aimsUuid, true)),
      );
      const activatedIds = scheduledIds.filter((_, i) => results[i]?.success);
      const failedIds = scheduledIds.filter((_, i) => !results[i]?.success);

      LogManager.metric("ScheduledAgents_Activated", activatedIds.length);
      if (activatedIds.length > 0) {
        onAgentsActivatedRef.current?.(activatedIds);
      }
      if (failedIds.length > 0) {
        LogManager.error(`Auto-activate scheduled agents: ${failedIds.length} failed to activate`);
        LogManager.metric("ScheduledAgents_ActivateFailed", failedIds.length);
        // Case 3 (all failed) reads the same as Case 1 → { scope: "all" }.
        // Case 2 (some failed) names the failed agents → { scope: "partial", failedIds }.
        onErrorRef.current?.(
          activatedIds.length === 0 ? { scope: "all" } : { scope: "partial", failedIds },
        );
      }
    };

    activate();
  }, [isAssistantEnabled, role, summarizeState, meetingUuid, meetingId, aimsUuid, scheduledIds, fetchFailed]);
};

export default useAutoActivateScheduledAgents;
